import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Eye, Pencil, KeyRound, Ban, CheckCircle2, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import Avatar from "@/components/Avatar";
import EmptyState from "@/components/EmptyState";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY_FORM = { name: "", email: "", phone: "", username: "", password: "", status: "active" };

export default function StaffPage() {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editForm, setEditForm] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("active");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff");
      setStaff(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addStaff = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      toast.error("Name, email and password are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/staff", { ...form, username: form.username.trim() || undefined });
      toast.success(`Staff member "${form.name}" created.`);
      setAddOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/staff/${editTarget.id}`, editForm);
      toast.success("Staff member updated.");
      setEditTarget(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const doResetPassword = async () => {
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    setSaving(true);
    try {
      await api.post(`/staff/${resetTarget.id}/reset-password`, { password: newPassword });
      toast.success(`Password reset for ${resetTarget.name}.`);
      setResetTarget(null);
      setNewPassword("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const doToggle = async () => {
    const next = toggleTarget.status === "active" ? "inactive" : "active";
    try {
      await api.post(`/staff/${toggleTarget.id}/status`, { status: next });
      toast.success(`Staff member ${next === "active" ? "activated" : "deactivated"}.`);
      setToggleTarget(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/staff/${deleteTarget.id}`);
      toast.success(`Staff member "${deleteTarget.name}" permanently deleted.`);
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const activeCount = staff.filter((s) => s.status === "active").length;
  const visibleStaff = staff.filter((s) => (tab === "active" ? s.status === "active" : s.status !== "active"));

  return (
    <div className="space-y-5" data-testid="staff-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="staff-title">Staff</h1>
          <p className="text-sm text-slate-500 mt-1">Manage staff accounts and their access.</p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="add-staff-button" className="bg-primary hover:bg-[#152D53] rounded-sm">
          <Plus className="h-4 w-4 mr-2" /> Add Staff
        </Button>
      </div>

      <div className="flex gap-1 bg-white border border-slate-200 rounded-md p-1 w-fit" data-testid="staff-tabs">
        <button
          onClick={() => setTab("active")}
          data-testid="staff-tab-active"
          className={`px-4 py-1.5 rounded-sm text-xs font-semibold transition-colors duration-150 ${tab === "active" ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-50"}`}
        >
          Active ({activeCount})
        </button>
        <button
          onClick={() => setTab("inactive")}
          data-testid="staff-tab-inactive"
          className={`px-4 py-1.5 rounded-sm text-xs font-semibold transition-colors duration-150 ${tab === "inactive" ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-50"}`}
        >
          Inactive ({staff.length - activeCount})
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        {loading ? (
          <div className="p-6 space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded-sm animate-pulse" />)}</div>
        ) : visibleStaff.length === 0 ? (
          <EmptyState title={tab === "active" ? "No active staff" : "No inactive staff"} message={tab === "active" ? "Add staff members so they can create and manage orders." : "Deactivated staff will appear here and can be permanently deleted."} testId="staff-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="staff-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {["Name", "Email", "Username", "Phone", "Role", "Orders Created", "Status", ""].map((h, i) => (
                    <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleStaff.map((s) => (
                  <tr key={s.id} className="border-b border-slate-100 even:bg-slate-50/60 hover:bg-blue-50/40 transition-colors duration-100" data-testid={`staff-row-${s.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2.5">
                        <Avatar name={s.name} userId={s.id} avatarPath={s.avatar_path} size="h-8 w-8" textSize="text-xs" testId={`staff-avatar-${s.id}`} />
                        <span className="font-semibold text-slate-900">{s.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{s.email}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{s.username || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap tnum">{s.phone || "—"}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-sm text-xs font-medium border bg-[#1B3A6B]/10 text-[#1B3A6B] border-[#1B3A6B]/30">Staff</span></td>
                    <td className="px-4 py-3 text-slate-800 tnum">{s.order_count}</td>
                    <td className="px-4 py-3">
                      <span data-testid={`staff-status-${s.id}`} className={`px-2 py-0.5 rounded-sm text-xs font-medium border ${s.status === "active" ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                        {s.status === "active" ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button title="View orders" data-testid={`staff-orders-${s.id}`} onClick={() => navigate(`/orders?staff_id=${s.id}`)} className="p-1.5 rounded-sm text-slate-500 hover:text-primary hover:bg-primary/10 transition-colors duration-150"><Eye className="h-4 w-4" /></button>
                        <button title="Edit" data-testid={`staff-edit-${s.id}`} onClick={() => { setEditTarget(s); setEditForm({ name: s.name, email: s.email, phone: s.phone || "" }); }} className="p-1.5 rounded-sm text-slate-500 hover:text-primary hover:bg-primary/10 transition-colors duration-150"><Pencil className="h-4 w-4" /></button>
                        <button title="Reset password" data-testid={`staff-reset-pw-${s.id}`} onClick={() => setResetTarget(s)} className="p-1.5 rounded-sm text-slate-500 hover:text-primary hover:bg-primary/10 transition-colors duration-150"><KeyRound className="h-4 w-4" /></button>
                        <button title={s.status === "active" ? "Deactivate" : "Activate"} data-testid={`staff-toggle-${s.id}`} onClick={() => setToggleTarget(s)} className={`p-1.5 rounded-sm transition-colors duration-150 ${s.status === "active" ? "text-slate-500 hover:text-[#E63946] hover:bg-[#E63946]/10" : "text-slate-500 hover:text-green-600 hover:bg-green-50"}`}>
                          {s.status === "active" ? <Ban className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                        </button>
                        {s.status !== "active" && (
                          <button title="Delete permanently" data-testid={`staff-delete-${s.id}`} onClick={() => setDeleteTarget(s)} className="p-1.5 rounded-sm text-slate-500 hover:text-[#E63946] hover:bg-[#E63946]/10 transition-colors duration-150">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-2xl" data-testid="add-staff-dialog">
          <DialogHeader><DialogTitle>Add Staff</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="space-y-2"><Label>Full Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-staff-name" className="border-slate-300 rounded-sm" /></div>
            <div className="space-y-2"><Label>Email (login) *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="add-staff-email" className="border-slate-300 rounded-sm" /></div>
            <div className="space-y-2"><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="add-staff-username" placeholder="Defaults to email prefix" className="border-slate-300 rounded-sm" /></div>
            <div className="space-y-2"><Label>Phone Number</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="add-staff-phone" className="border-slate-300 rounded-sm" /></div>
            <div className="space-y-2"><Label>Temporary Password *</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="add-staff-password" className="border-slate-300 rounded-sm" /></div>
            <div className="space-y-2"><Label>Role</Label><Input value="Staff" disabled className="border-slate-200 rounded-sm bg-slate-50 text-slate-600" data-testid="add-staff-role" /></div>
            <div className="space-y-2"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="add-staff-status" className="border-slate-300 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="add-staff-cancel" className="rounded-sm">Cancel</Button>
            <Button onClick={addStaff} disabled={saving} data-testid="add-staff-submit" className="bg-primary hover:bg-[#152D53] rounded-sm">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Staff"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editTarget)} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent data-testid="edit-staff-dialog">
          <DialogHeader><DialogTitle>Edit Staff</DialogTitle></DialogHeader>
          {editForm && (
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Full Name</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-staff-name" className="border-slate-300 rounded-sm" /></div>
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-staff-email" className="border-slate-300 rounded-sm" /></div>
              <div className="space-y-2"><Label>Phone</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="edit-staff-phone" className="border-slate-300 rounded-sm" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} data-testid="edit-staff-cancel" className="rounded-sm">Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="edit-staff-submit" className="bg-primary hover:bg-[#152D53] rounded-sm">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(resetTarget)} onOpenChange={(o) => { if (!o) { setResetTarget(null); setNewPassword(""); } }}>
        <DialogContent data-testid="reset-staff-password-dialog">
          <DialogHeader><DialogTitle>Reset Password — {resetTarget?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <Label>New Password</Label>
            <Input value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="reset-staff-password-input" placeholder="Minimum 6 characters" className="border-slate-300 rounded-sm" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)} data-testid="reset-staff-password-cancel" className="rounded-sm">Cancel</Button>
            <Button onClick={doResetPassword} disabled={saving} data-testid="reset-staff-password-submit" className="bg-primary hover:bg-[#152D53] rounded-sm">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset Password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title={`Permanently delete ${deleteTarget?.name}?`}
        description="This staff account will be permanently deleted and can no longer log in. Orders they created remain in the system."
        confirmLabel="Delete Permanently"
        destructive
        onConfirm={doDelete}
        testId="delete-staff-dialog"
      />

      <ConfirmDialog
        open={Boolean(toggleTarget)}
        onOpenChange={(o) => !o && setToggleTarget(null)}
        title={`${toggleTarget?.status === "active" ? "Deactivate" : "Activate"} ${toggleTarget?.name}?`}
        description={toggleTarget?.status === "active"
          ? "This staff member will no longer be able to log in."
          : "This staff member will be able to log in again."}
        confirmLabel={toggleTarget?.status === "active" ? "Deactivate" : "Activate"}
        destructive={toggleTarget?.status === "active"}
        onConfirm={doToggle}
        testId="toggle-staff-dialog"
      />
    </div>
  );
}
